// @flow
import { useRef, useCallback, type Node } from 'react';
import { animated, useSpring } from 'react-spring/web.cjs';
import LazyLoad from 'react-lazyload';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { SPACING, COLORS } from '@tablekit/theme';
import {
  DESKTOP_BREAKPOINT,
  LAPTOP_BREAKPOINT,
  MOBILE_BREAKPOINT,
  LAZY_LOAD_OFFSET,
  MOBILE_MAX_WIDTH
} from 'utils/theme';
import useOnScroll from 'utils/useOnScroll';
import columnWidth from 'utils/columnWidth';
import { Grid, Cell } from 'styled/Grid';
import { imageTransform } from './Image';

const imageHeight = 640;

const RestrictedCell = styled(Cell)`
  position: relative;
  overflow: visible;

  @media ${DESKTOP_BREAKPOINT} {
    max-height: ${imageHeight}px;
  }
`;

export function isRightCheck<T>(isRightOption: T, isLeftOption: T): (*) => T {
  return ({ isRight, theme: { isRtl } = {} }) =>
    (!isRtl && !isRight) || (isRtl && isRight) ? isLeftOption : isRightOption;
}

const ImageWrapperCell = styled(RestrictedCell)`
  align-items: center;
`;

const PaddedGrid = styled(Grid)`
  margin: ${SPACING.L6} 0;
  overflow: visible;

  & > div {
    ${({ isRight }) => css`
      flex-direction: ${isRight ? 'row-reverse' : 'row'};
    `};
    @media ${MOBILE_BREAKPOINT} {
      flex-direction: column;

      & > * {
        width: 100%;
        max-width: 100%;
        max-height: none;
      }
    }
  }
`;

const Image = styled.img`
  max-height: ${imageHeight}px;
  max-width: 100%;

  @media ${LAPTOP_BREAKPOINT} {
    max-width: ${columnWidth(10)}%;
    align-self: ${isRightCheck('flex-start', 'flex-end')};
  }

  @media ${DESKTOP_BREAKPOINT} {
    max-width: ${columnWidth(8)}%;
    align-self: center;
  }
`;

const ParallaxImage = animated(styled.div`
  position: absolute;
  border-radius: 20px;
  background: linear-gradient(-45deg, ${COLORS.GRAY.L3}, ${COLORS.GRAY.L1});
  ${isRightCheck('left: 57%', 'left: 43%')};
  width: 60%;
  height: 110%;
  top: 50%;
  z-index: -1;

  @media ${MOBILE_BREAKPOINT} {
    height: 100%;
    left: ${isRightCheck('70%', '30%')};
  }
`);

const maxParallaxPercentage = 60;
const minParallaxPercentage = 40;

const ContentWithImage = ({
  children,
  image,
  imageAlt,
  hideParallax = false,
  isRight = true,
  isBackgroundDisabled = false
}: {
  children: Node,
  hideParallax?: boolean,
  image: string,
  imageAlt: string,
  isBackgroundDisabled?: boolean,
  isRight?: boolean
}) => {
  const parallaxRef = useRef();
  let parallaxNode;
  if (!hideParallax && !isBackgroundDisabled) {
    const [styles, setSpring] = useSpring(() => ({
      offsetPercentage: -minParallaxPercentage,
      tension: 500
    }));
    const { offsetPercentage } = styles;
    const updateOnScroll = useCallback(() => {
      if (!parallaxRef.current) return;
      const windowCenter = window.innerHeight / 2;
      const { top, height } = parallaxRef.current.getBoundingClientRect();
      const thisCenter = top + height / 2;
      const travelPercentage =
        Math.min(2, Math.max(0, thisCenter / windowCenter)) / 2;
      setSpring({
        offsetPercentage:
          -maxParallaxPercentage +
          travelPercentage * (maxParallaxPercentage - minParallaxPercentage)
      });
    }, [parallaxRef, setSpring]);
    useOnScroll(updateOnScroll);
    parallaxNode = (
      <ParallaxImage
        isRight={isRight}
        alt="Parallax Image"
        style={{
          transform: offsetPercentage.interpolate(
            (n) => `translate(-50%, ${n}%)`
          )
        }}
      />
    );
  }

  // parallaxImage is cropped in CMS level
  const imageColumn = (
    <ImageWrapperCell key="image" span={6}>
      {parallaxNode}
      <LazyLoad height={imageHeight} offset={LAZY_LOAD_OFFSET}>
        <Image
          ref={parallaxRef}
          src={imageTransform(image, {
            size: { width: MOBILE_MAX_WIDTH, height: imageHeight }
          })}
          alt={imageAlt}
          isRight={isRight}
        />
      </LazyLoad>
    </ImageWrapperCell>
  );
  const contentColumn = (
    <RestrictedCell key="content" span={6}>
      {children}
    </RestrictedCell>
  );
  return (
    <PaddedGrid isRight={isRight}>
      {imageColumn}
      {contentColumn}
    </PaddedGrid>
  );
};

export default ContentWithImage;
